import { useMotionValueEvent, useScroll } from 'framer-motion';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import DownloadSVG from 'public/icons/download.svg';
import PdfSVG from 'public/icons/pdf.svg';
import RoundArrowSVG from 'public/icons/round-arrow.svg';
import { useRef, useState } from 'react';
import { PAGES } from '@/consts';
import { TeamPerson } from '@/entities';
import { Day } from '@/entities/day';
import { Direction } from '@/entities/direction';
import { Format } from '@/entities/format';
import { Place } from '@/entities/place';
import { getCommonPageProps } from '@/shared/api';
import { getDays } from '@/shared/api/days';
import { getDirections } from '@/shared/api/directions';
import { getEmployees } from '@/shared/api/employees';
import { getPageDetail } from '@/shared/api/page-detail';
import { getFormats } from '@/shared/api/participation-formats';
import { getPlaces } from '@/shared/api/places';
import { ApiProgramRequest, AppProgramResponse, getProgram } from '@/shared/api/program';
import { getQuestions } from '@/shared/api/questions';
import { getSiteSettings } from '@/shared/api/site-settings';
import { useHeaderThemeValue } from '@/shared/atoms/header-theme';
import DefaultLayout from '@/shared/components/layout/DefaultLayout';
import SubscribePopup from '@/shared/components/popups/SubscribePopup';
import FaqSection, { FaqSectionProps } from '@/shared/components/sections/FaqSection';
import FreeEventsSection, {
    FreeEventsSectionProps,
} from '@/shared/components/sections/FreeEventsSection/FreeEventsSection';
import ProgramDetailSection from '@/shared/components/sections/ProgramDetailSection';
import {
    ProgramDetailSectionProps,
    ProgramDetailSectionRef,
} from '@/shared/components/sections/ProgramDetailSection/ProgramDetailSection';
import QuestionsSection, { QuestionsSectionProps } from '@/shared/components/sections/QuestionsSection';
import Breadcrumbs from '@/shared/components/shared/Breadcrumbs';
import Button from '@/shared/components/shared/Button';
import Link from '@/shared/components/shared/Link';
import ScrollTopButton from '@/shared/components/shared/ScrollTopButton';
import Typografed from '@/shared/components/shared/Typografed';
import { CommonPageProps, NonUndefined } from '@/shared/lib/types';

const ProgramPage = ({
    h1,
    subtitle,
    breadcrumbs,
    programDetailSectionProps,
    places,
    days,
    formats,
    directions,
    initialParams = {},
    initialProgramResponse,
    freeEventsSectionProps,
    questionsSectionProps,
    contactPersonProps,
    faqSectionProps,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const ref = useRef(null);
    const [isButtonVisible, setIsButtonVisible] = useState(false);
    const theme = useHeaderThemeValue();
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] });

    useMotionValueEvent(scrollYProgress, 'change', (progress) => {
        setIsButtonVisible(progress > 0);
    });
    const programDetailRef = useRef<ProgramDetailSectionRef>(null);

    const handleDownloadPDF = () => {
        if (programDetailRef.current) {
            programDetailRef.current.triggerFunction();
        }
    };

    return (
        <DefaultLayout currentPageId={PAGES.PROGRAM} ref={ref}>
            <div className="news-page-top js-header-theme-trigger wrapper" data-theme="dark" id="top">
                <Breadcrumbs className="news-page-breadcrumbs" breadcrumbs={breadcrumbs} />
                <div className="program-detail-title-block">
                    <div className="program-detail-title-block-text">
                        {subtitle && <div className="program-main-article text-m">{subtitle}</div>}
                        <h1 className="program-detail-title h2">
                            <Typografed>{h1}</Typografed>
                        </h1>
                    </div>
                    <div className="program-detail-title-btns">
                        <Link href="/tour-tnf/#tour" className="program-detail-360-btn text-xs">
                            360°
                            <RoundArrowSVG className="round-arrow-svg" />
                        </Link>
                        <Button
                            className="favorite-window-download-btn js-favorite-window-download-btn"
                            onClick={handleDownloadPDF}
                        >
                            <div className="presentation-btn-left">
                                <PdfSVG className="pdf-svg" />
                                <div className="text-xs">Скачать программу</div>
                            </div>
                            <div className="download-loader-wrapper">
                                <div className="download-loader"></div>
                            </div>
                            <DownloadSVG className="download-svg" />
                            <div className="presentation-btn-error text-xs">Что то пошло не так</div>
                        </Button>
                    </div>
                </div>
            </div>

            <ProgramDetailSection
                ref={programDetailRef}
                reflectFilterStateInUrl
                programDetailSectionProps={programDetailSectionProps}
                isButtonVisible={isButtonVisible}
                days={days}
                directions={directions}
                places={places}
                formats={formats}
                initialParams={initialParams}
                initialResponse={initialProgramResponse}
                mainTheme={h1}
            />
            {freeEventsSectionProps.cards && freeEventsSectionProps.cards.length > 0 && (
                <FreeEventsSection freeEventsSectionProps={freeEventsSectionProps} data-theme="dark" id="free-events" />
            )}
            {faqSectionProps.questions && faqSectionProps.questions.length > 0 && (
                <FaqSection faqSectionProps={faqSectionProps} />
            )}
            <QuestionsSection
                questionsSectionProps={questionsSectionProps}
                contactPersonProps={contactPersonProps}
                className="js-header-theme-trigger"
                data-theme="dark"
                id="registration"
            />
            <SubscribePopup />
            <ScrollTopButton theme={theme} scrollYProgress={scrollYProgress} universal />
        </DefaultLayout>
    );
};

export default ProgramPage;

type ProgramPageProps = NonUndefined<
    CommonPageProps & {
        h1: string;
        subtitle: string;
        programDetailSectionProps: ProgramDetailSectionProps;
        days: Day[];
        formats: Format[];
        places: Place[];
        directions: Direction[];
        initialParams: ApiProgramRequest;
        initialProgramResponse: AppProgramResponse;
        freeEventsSectionProps: FreeEventsSectionProps;
        questionsSectionProps: QuestionsSectionProps;
        contactPersonProps: TeamPerson | null;
        faqSectionProps: FaqSectionProps;
    }
>;

export const getServerSideProps: GetServerSideProps<ProgramPageProps> = async ({ query }) => {
    const pageResponse = await getPageDetail({ params: { slug: 'program' } });
    const initialParams: ApiProgramRequest = {};
    const [
        commonPageProps,
        [days, programDetailResponse, freeEventsResponse],
        placesResponse,
        directionsResponse,
        formatsResponse,
        questionsResponse,
        employeesResponse,
        siteSettings,
    ] = await Promise.all([
        getCommonPageProps(),
        getDays({ params: { page: pageResponse.data?.id ?? PAGES.PROGRAM } }).then((daysResponse) => {
            const days = daysResponse.data || [];
            const dayIds = days.map((day) => day.id);

            if (typeof query.day === 'string') {
                initialParams.day = parseInt(query.day);
            }

            if (typeof query.directions === 'string') {
                initialParams.directions = query.directions.split(',').map((id) => parseInt(id.trim()));
            }

            if (typeof query.participation_formats === 'string') {
                initialParams.participation_formats = query.participation_formats
                    .split(',')
                    .map((id) => parseInt(id.trim()));
            }

            if (typeof query.q === 'string') {
                initialParams.q = query.q;
            }

            return Promise.all([
                days,
                getProgram({
                    params: {
                        ...initialParams,
                        days: typeof initialParams.day === 'number' ? [initialParams.day] : days.map((day) => day.id),
                    },
                }),
                getProgram({
                    params: {
                        days: dayIds,
                        is_free: 1,
                    },
                }),
            ]);
        }),
        getPlaces(),
        getDirections(),
        getFormats({ params: { page: pageResponse.data?.id ?? PAGES.PROGRAM } }),
        getQuestions({ params: { page: pageResponse.data?.id ?? PAGES.PROGRAM } }),
        getEmployees({ params: { page: pageResponse.data?.id ?? PAGES.PROGRAM } }),
        getSiteSettings({
            params: {
                keys: [
                    'program_first_screen_title',
                    'program_first_screen_subtitle',

                    'program_other_event_title',
                    'program_other_event_description',
                    'program_other_event_button_text',

                    'program_faq_title',

                    'program_form_title',
                    'program_form_description',
                ],
            },
        }),
    ]);

    const formats = formatsResponse.data || [];
    const directions = directionsResponse.data || [];
    const places = placesResponse.data || [];
    const freeEventsCards = (freeEventsResponse.data?.items || []).map((event) => {
        const matchingDay = days.find((day) => day.id === event.day);
        const dayName = matchingDay ? matchingDay.day : '';

        return {
            date: `${dayName}, ${event.time}`,
            title: event.name,
            text: event.description,
            img: event.image,
        };
    });
    return {
        props: {
            ...commonPageProps,
            bodyClass: 'program-page',
            meta: {
                ...commonPageProps.meta,
                ...(pageResponse.data?.meta ?? []),
            },
            breadcrumbs: [
                { text: 'TNF', href: '/' },
                { text: 'Программа', href: '/program/' },
            ],
            h1: siteSettings.data?.program_first_screen_title || 'Технологическая устойчивость: стратегия развития',
            subtitle: siteSettings.data?.program_first_screen_subtitle || 'Главная тема форума',
            programDetailSectionProps: {
                cards: programDetailResponse.data?.items || [],
            },
            directions,
            formats,
            days,
            places,
            initialParams,
            initialProgramResponse: programDetailResponse,
            freeEventsSectionProps: {
                title: siteSettings.data?.program_other_event_title || '',
                subtitle: siteSettings.data?.program_other_event_description || '',
                cards: freeEventsCards,
                buttonText: siteSettings.data?.program_other_event_button_text || '',
            },
            questionsSectionProps: {
                title: siteSettings.data?.program_form_title ?? 'Остались вопросы? Напишите нам',
                description: siteSettings.data?.program_form_description ?? '',
            },
            contactPersonProps: employeesResponse.data?.[0] || null,
            faqSectionProps: {
                title: siteSettings.data?.program_faq_title ?? '',
                questions: questionsResponse.data ?? [],
            },
        },
    };
};
